import { lazy, memo, useEffect, useState } from 'react';
import { AppDispatch, AppState } from '../../../store';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Pagination, Stack } from '@mui/material';
import { useAuth } from 'oidc-react';
import { useTranslation } from 'react-i18next';
import {
  RecordsPerPage,
  EFilterRootOption,
  EConfigurationStateCode,
} from '../../../data/Constants';

import {
  getCategories,
  getMyConfigurations,
  updateMyConfigPageDetails,
} from '../../../store/states/UserSlice';
import { filterIds, prepareMyConfigurationTreeCodes } from '../../../store/Helpers/LandingHelper';
import { SelectComponent } from '../../InputComponents/SelectComponent';
import { ConfigurationCardItem } from './ConfigurationCardItem';
import type {
  IFilterOption,
  IMyConfiguration,
  IMyConfigurationFilter,
  IMyConfigurationFilterOptions,
  IMyConfigurationParams,
  ISimpleTreeProps,
} from '../../../../types';
import type { IMyConfigurationRequest } from '../../../../types/IApiTypes';

const SimpleTreeViewComponent = lazy(() => import('../../SimpleTreeViewComponent'));

/**
 * This component perform the role to show the configuration, filter and pagination.
 * @returns {JSX.Element} return the Myconfigurations component
 */
const MyConfigurations = () => {
  const initialFilterOptionValue = { channels: [], applications: [], states: [] };
  const auth = useAuth();
  const token = auth?.userData?.access_token ?? '';
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  //selectors
  const configuration = useSelector((state: AppState) => state.configuration);
  const user = useSelector((state: AppState) => state.user);

  const [myConfigs, setMyConfigs] = useState<IMyConfiguration[]>([]);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(RecordsPerPage[0]);
  const [totalMyConfigRecords, setTotalMyConfigRecords] = useState<number>(1);
  const [myConfigPage, setMyConfigPage] = useState<number>(1);
  const [filterOptions, setFilterOptions] =
    useState<IMyConfigurationFilterOptions>(initialFilterOptionValue);
  const [availableFilterCodes, setAvailableFilterCodes] =
    useState<IMyConfigurationFilter>(initialFilterOptionValue);
  const [filteredCodes, setFilteredCodes] =
    useState<IMyConfigurationFilter>(initialFilterOptionValue);
  const [isInitialStateLoad, setIsInitialStateLoad] = useState<boolean>(true);

  /**
   * Fetches the user's configurations based on page, limit, search value, and selected codes.
   * @param {IMyConfigurationParams} params - Parameters for fetching configurations.
   * @param {number} params.page - Current page number.
   * @param {number} params.limit - Number of records per page.
   * @param {string} params.searchKey - Search value for configurations.
   * @param {IMyConfigurationFilter} [params.selectedCodes] - Selected filter codes.
   * @param {boolean} [params.sendEmptyStates] - Flag to send empty states array on initial load.
   * @returns {void}
   */
  const fetchMyConfigurations = ({
    page,
    limit,
    searchKey,
    selectedCodes = filteredCodes,
  }: IMyConfigurationParams): void => {
    const applications = filterIds(selectedCodes.applications, EFilterRootOption.Application);
    const channels = filterIds(selectedCodes.channels, EFilterRootOption.Channel);
    // On initial load, send empty array for states, otherwise send filtered states
    const states = filterIds(selectedCodes.states, EFilterRootOption.States);
    const params: IMyConfigurationRequest = {
      token: token,
      page: page,
      limit: limit,
      searchKey: searchKey,
      applicationIds: applications,
      channelCodes: channels,
      states: states,
    };
    dispatch(getMyConfigurations(params));
  };

  /**
   * Fetches available categories for filtering configurations.
   * @returns {void}
   */
  const fetchCategories = (): void => {
    dispatch(getCategories({ token: token }));
  };

  /**
   * Handles pagination change event.
   * @param {React.ChangeEvent<unknown>} _event - Pagination change event.
   * @param {number} value - Current selected page.
   * @returns {void}
   */
  const handleChange = (_event: React.ChangeEvent<unknown>, value: number): void => {
    const { applications, channels } = filteredCodes;
    const { totalRecords } = user.myConfigPageDetails;
    if (user.myConfigurations[value]) {
      setMyConfigs(user.myConfigurations[value]);
    } else {
      fetchMyConfigurations({
        page: value,
        limit: recordsPerPage,
        searchKey: configuration.search.key ?? '',
      });
    }
    setMyConfigPage(value);
    dispatch(
      updateMyConfigPageDetails({
        page: value,
        limit: recordsPerPage,
        totalRecords: totalRecords,
        applicationIds: applications,
        channelCodes: channels,
      })
    );
  };

  /**
   * Handles change in records per page selection.
   * @param {React.ChangeEvent} event - Change event for records per page.
   * @returns {void}
   */
  const handleChangeForRPP = (event: React.ChangeEvent): void => {
    const value = Number((event.target as HTMLInputElement).value);
    setRecordsPerPage(value);
    fetchMyConfigurations({
      page: 1,
      limit: value,
      searchKey: configuration.search.key ?? '',
    });
  };

  /**
   * Fetches configurations and categories on initial component mount.
   */
  useEffect(() => {
    fetchCategories();
  }, []);

  /**
   * Updates state when configurations or categories change.
   */
  useEffect(() => {
    const { page, limit, totalRecords } = user.myConfigPageDetails;
    setMyConfigPage(page);
    setRecordsPerPage(limit);
    setTotalMyConfigRecords(Math.ceil(totalRecords / limit) || 1);
    setMyConfigs(user.myConfigurations[page] || []);
    if (user.category) {
      setFilterOptions(user.category);
    }
    const filterCodes = prepareMyConfigurationTreeCodes(user.category);
    setAvailableFilterCodes(filterCodes);

    // On initial load, set state filters to exclude 'UnAvailable'
    // Only run this if it's the initial load and category data is available
    if (isInitialStateLoad && filterCodes.states.length > 0) {
      const statesWithoutUnavailable = filterCodes.states.filter(
        (stateCode) => stateCode !== EConfigurationStateCode.UnAvailable
      );
      const initialFilteredCodes: IMyConfigurationFilter = {
        channels: [],
        applications: [],
        states: statesWithoutUnavailable,
      };
      setFilteredCodes(initialFilteredCodes);
      setIsInitialStateLoad(false); // Mark initial load as complete

      // Fetch configurations with empty states array on initial load
      fetchMyConfigurations({
        page: page,
        limit: limit,
        searchKey: '',
        selectedCodes: { ...initialFilteredCodes, states: [] },
      });
    }
  }, [user.myConfigurations, user.category]);

  /**
   * The filterTreeProps is the schema for the creating filter tree on the myconfiguration left pane
   */

  const filterTreeProps: ISimpleTreeProps = {
    treeNodes: [
      {
        type: EFilterRootOption.Channel.toLowerCase(),
        enumValue: EFilterRootOption.Channel,
        label: t('labels.channel'),
        expanded: EFilterRootOption.Channel,
        treeItems: filterOptions.channels,
      },
      {
        type: EFilterRootOption.States.toLowerCase(),
        enumValue: EFilterRootOption.States,
        label: t('labels.state'),
        expanded: EFilterRootOption.States,
        treeItems: filterOptions.states,
      },
      {
        type: EFilterRootOption.Application.toLowerCase(),
        enumValue: EFilterRootOption.Application,
        label: t('labels.application'),
        expanded: EFilterRootOption.Channel,
        treeItems: filterOptions.applications,
      },
    ],
    availableFilterCodes: availableFilterCodes as unknown as IFilterOption,
    filteredCodes: filteredCodes as unknown as IFilterOption,
    initialExpandValue: { channels: true, applications: false, states: true },
    filterDataAvailable: filterOptions.applications.length > 0 || filterOptions.channels.length > 0,
  };

  /**
   * Handles change in filter tree selection from the left pane.
   * @param {IFilterOption} selectedCodes - Selected codes from the filter section.
   * @returns {void}
   */
  const handleFilteredCodes = (selectedCodes: IFilterOption): void => {
    // Convert IFilterOption to IMyConfigurationFilter
    const convertedCodes: IMyConfigurationFilter = {
      channels: selectedCodes.channels ?? [],
      applications: selectedCodes.applications ?? [],
      states: selectedCodes.states ?? [],
    };
    const { page, limit } = user.myConfigPageDetails;
    setFilteredCodes(convertedCodes);
    fetchMyConfigurations({
      page: page,
      limit: limit,
      searchKey: configuration.search.key ?? '',
      selectedCodes: convertedCodes,
    });
  };

  // Helper to render configuration cards
  const renderConfigurationCards = (configs: IMyConfiguration[]) => (
    <Box className="myConfigurations-list-Tab">
      <Grid container spacing={1}>
        {configs.map((config: IMyConfiguration, index: number) => (
          <ConfigurationCardItem key={config.configurationId} config={config} index={index} />
        ))}
      </Grid>
    </Box>
  );

  /**
   * Returns the label to display when no configurations are found.
   * @returns {React.ReactElement}
   */
  const getNoConfigurationFoundLabel = (): React.ReactElement => {
    // Check if there are any filtered codes or a product search value
    const hasFilteredCodes =
      filteredCodes.applications?.length != 0 ||
      filteredCodes.channels?.length != 0 ||
      filteredCodes.states?.length != 0 ||
      configuration.search.key;
    return (
      <span className="noData-label" data-testid="noConfigurationLabel">
        {hasFilteredCodes ? t('labels.noDataFoundForCriteria') : t('labels.noDataFound')}
      </span>
    );
  };

  return (
    <Box className="product-portfolio-container">
      <Box className="hierarchy-container" style={{ marginTop: '25px' }}>
        {user.loading ? (
          <div className="loader-placeholder" data-testid="loaderPlaceholder">
            {t('loaderMessage.default')} ...
          </div>
        ) : (
          <SimpleTreeViewComponent
            filterTreeProps={filterTreeProps}
            handleChanges={handleFilteredCodes}
          />
        )}
      </Box>

      {user.myConfigurations[user.myConfigPageDetails.page]?.length === 0 ? (
        getNoConfigurationFoundLabel()
      ) : (
        <div className="myConfigurations-Tab">
          <Box className="recordsChange-conatiner">
            <SelectComponent
              name="recordsPerPage"
              cssStyle="recordsPerPage"
              value={recordsPerPage}
              options={RecordsPerPage}
              onChangeCallBack={handleChangeForRPP}
            />
          </Box>
          {renderConfigurationCards(myConfigs)}
          <Stack spacing={1} className="myConfig-pagination">
            <Pagination
              color="primary"
              count={totalMyConfigRecords}
              page={myConfigPage}
              onChange={handleChange}
            />
          </Stack>
        </div>
      )}
    </Box>
  );
};

export default memo(MyConfigurations);
